import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users, Gift, Package, Clock, Download, Trash2, LogOut, Search, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  pending: "bg-gold/25 text-[#8A6100] border-gold/60",
  contacted: "bg-ice/10 text-ice border-ice/50",
  shipped: "bg-green-500/15 text-green-700 border-green-500/40",
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-midnight border border-steel/60 rounded-lg p-6">
    <div className="flex items-center justify-between mb-4">
      <span className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-cream/50">{label}</span>
      <Icon className="w-5 h-5 text-crimson" />
    </div>
    <p className="font-display font-black text-4xl">{value}</p>
  </div>
);

export default function Dashboard() {
  const { user, logout, authFetch } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const loadLeads = useCallback(async () => {
    try {
      const res = await authFetch((t) => axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t}` } }));
      setLeads(res.data);
    } catch (e) {
      toast.error("Could not load leads. Log in again.");
      logout();
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [authFetch, logout, navigate]);

  useEffect(() => {
    if (user === false) {
      navigate("/admin");
      return;
    }
    if (user) loadLeads();
  }, [user, navigate, loadLeads]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(
      (l) =>
        l.full_name.toLowerCase().includes(q) ||
        l.city.toLowerCase().includes(q) ||
        l.state.toLowerCase().includes(q)
    );
  }, [leads, query]);

  const stats = useMemo(
    () => ({
      total: leads.length,
      gifts: leads.filter((l) => l.for_whom === "gift").length,
      bundle: leads.filter((l) => l.bundle_offer === "yes").length,
      pending: leads.filter((l) => l.status === "pending").length,
    }),
    [leads]
  );

  const updateStatus = async (id, status) => {
    try {
      await authFetch((t) =>
        axios.patch(`${API}/leads/${id}`, { status }, { headers: { Authorization: `Bearer ${t}` } })
      );
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Status update failed.");
    }
  };

  const deleteLead = async (id) => {
    try {
      await authFetch((t) => axios.delete(`${API}/leads/${id}`, { headers: { Authorization: `Bearer ${t}` } }));
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await authFetch((t) =>
        axios.get(`${API}/leads/export`, { headers: { Authorization: `Bearer ${t}` }, responseType: "blob" })
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "master-blaster-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported to CSV.");
    } catch {
      toast.error("Export failed.");
    }
  };

  if (user === null || (user && loading)) {
    return (
      <div className="min-h-screen bg-navy text-cream flex items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-crimson" />
      </div>
    );
  }
  if (!user) return null;

  return (
    <div data-testid="owner-dashboard" className="min-h-screen bg-navy text-cream">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-navy/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="dashboard-logo" className="flex items-center gap-3">
            <img src="/images/master-blaster-logo.png" alt="The Master Blaster logo" className="h-10 w-auto rounded-sm" />
            <span className="font-jbmono text-[10px] tracking-[0.25em] text-gold">Owner</span>
          </Link>
          <button
            onClick={() => { logout(); navigate("/admin"); }}
            data-testid="dashboard-logout-button"
            className="flex items-center gap-2 font-jbmono text-xs uppercase tracking-widest text-cream/60 hover:text-crimson transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
        <h1 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight mb-2">Pre-Order Leads</h1>
        <p className="text-sm text-cream/50 mb-10">Every reservation request from the landing page, saved here forever.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Users} label="Total Leads" value={stats.total} testid="stat-total-leads" />
          <StatCard icon={Gift} label="Gifts" value={stats.gifts} testid="stat-gifts" />
          <StatCard icon={Package} label="Freedom Pack" value={stats.bundle} testid="stat-bundle" />
          <StatCard icon={Clock} label="Pending" value={stats.pending} testid="stat-pending" />
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40" />
            <input
              data-testid="leads-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, city or state…"
              className="w-full bg-midnight border border-steel rounded-sm pl-11 pr-4 py-3 text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:border-crimson transition-colors duration-200"
            />
          </div>
          <button
            onClick={exportCsv}
            data-testid="export-csv-button"
            className="flex items-center justify-center gap-2 bg-gold hover:bg-gold/90 text-[#0B132B] font-display font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors duration-200"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        <div className="bg-midnight border border-steel/60 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="leads-table">
            <thead>
              <tr className="border-b border-steel/60 text-left">
                {["Date", "Name", "City / State", "Foot", "Why", "For", "Bundle", "Source", "Status", ""].map((h) => (
                  <th key={h} className="px-5 py-4 font-jbmono text-[10px] uppercase tracking-[0.2em] text-cream/50">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-5 py-12 text-center text-cream/40" data-testid="leads-empty-state">
                    No leads yet. Share the site and watch them blast in.
                  </td>
                </tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-steel/30 hover:bg-navy/50 transition-colors duration-150" data-testid={`lead-row-${l.id}`}>
                  <td className="px-5 py-4 font-jbmono text-xs text-cream/60 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-4 font-semibold whitespace-nowrap">{l.full_name}</td>
                  <td className="px-5 py-4 text-cream/70 whitespace-nowrap">{l.city}, {l.state}</td>
                  <td className="px-5 py-4 text-cream/70 capitalize whitespace-nowrap">{l.foot_choice || "both"}</td>
                  <td className="px-5 py-4 text-cream/60 max-w-[220px] truncate" title={l.why}>{l.why || "—"}</td>
                  <td className="px-5 py-4 text-cream/70 capitalize">{l.for_whom === "gift" ? "Gift" : "Self"}</td>
                  <td className="px-5 py-4 text-cream/70 capitalize">{l.bundle_offer}</td>
                  <td className="px-5 py-4 text-cream/70 capitalize">{l.hear_about}</td>
                  <td className="px-5 py-4">
                    <select
                      value={l.status}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      data-testid={`lead-status-${l.id}`}
                      className={`border rounded-sm px-2.5 py-1.5 text-xs font-jbmono uppercase tracking-wider bg-navy cursor-pointer focus:outline-none ${statusStyles[l.status] || statusStyles.pending}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="contacted">Contacted</option>
                      <option value="shipped">Shipped</option>
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => deleteLead(l.id)}
                      data-testid={`lead-delete-${l.id}`}
                      className="text-cream/40 hover:text-crimson transition-colors duration-200"
                      aria-label="Delete lead"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
